import { useCallback, useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Box, alpha, useMediaQuery, useTheme } from "@mui/material";
import Sidebar from "../components/Sidebar";
import Navbar from "../components/Navbar";
import HomeBackdrop from "../components/HomeBackdrop";
import { useAuthContext } from "../context/auth";
import { BRAND, isChatOnly } from "../utils/launcher";

// Page scrollbar in the same look as the DataGrid ones (useGridStyles): transparent track,
// thin rounded handle, accent while hovered/dragged
const pageScrollbarSx = (theme) => {
  const thumb = theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.22)' : 'rgba(0,0,0,0.22)';
  const accent = theme.palette.secondary.main;
  return {
    // Firefox only: in Chrome/Edge 121+ these would switch off the ::-webkit-scrollbar rules below
    '@supports not selector(::-webkit-scrollbar)': {
      scrollbarWidth: 'thin',
      scrollbarColor: `${thumb} transparent`,
    },
    '&::-webkit-scrollbar': { width: 10, height: 10, backgroundColor: 'transparent' },
    '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: thumb,
      borderRadius: 10,
      border: '3px solid transparent',
      backgroundClip: 'content-box',
      minHeight: 40,
    },
    '&::-webkit-scrollbar-thumb:hover': { backgroundColor: alpha(accent, 0.75), border: '2px solid transparent' },
    '&::-webkit-scrollbar-thumb:active': { backgroundColor: accent },
    '&::-webkit-scrollbar-corner': { backgroundColor: 'transparent' },
  };
};

const EXPANDED_WIDTH = 240;
const COLLAPSED_WIDTH = 80;
const SIDEBAR_KEY = "layout.sidebarOpen";

const homeBackground = {
  backgroundImage: `
    linear-gradient(rgba(5, 20, 40, 0.45), rgba(5, 20, 40, 0.55)),
    url("${BRAND.backgrounds[0]}")
  `,
  backgroundSize: "cover",
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",
  backgroundAttachment: "fixed",
};

const readSidebarPreference = () => {
  try {
    return localStorage.getItem(SIDEBAR_KEY) !== "0";
  } catch {
    return true;
  }
};

const TopLeft = () => {
  const { auth } = useAuthContext();
  const { pathname } = useLocation();

  const authRoles = auth?.roles ?? [];
  const isGuest = isChatOnly(authRoles); // Guest account, or any role inside the desktop launcher
  
  const isHomePage = pathname === "/";
  // Home, Login and Signup share the background picture
  const hasBackground = !auth && ["/", "/login", "/signup"].includes(pathname);
  // Home, Login and Signup (logged out) share the background slideshow
  const showSlideshow = hasBackground;
  const theme = useTheme();
  const isNonMobile = useMediaQuery("(min-width: 600px)", { noSsr: true });

  // Desktop: expanded / collapsed (remembered). Mobile: drawer open / closed (starts closed).
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => isNonMobile && readSidebarPreference());

  // Mobile: the drawer is an overlay, so close it after every navigation
  useEffect(() => {
    if (!isNonMobile) setIsSidebarOpen(false);
  }, [isNonMobile, pathname]);

  // Back on desktop: restore the saved choice
  useEffect(() => {
    if (isNonMobile) setIsSidebarOpen(readSidebarPreference());
  }, [isNonMobile]);

  // Same signature as a normal state setter, plus it remembers the desktop choice
  const handleSidebarOpen = useCallback(
    (next) => {
      const value = typeof next === "function" ? next(isSidebarOpen) : next;
      setIsSidebarOpen(value);

      if (isNonMobile) {
        try {
          localStorage.setItem(SIDEBAR_KEY, value ? "1" : "0");
        } catch {
          /* storage unavailable */
        }
      }
    },
    [isNonMobile, isSidebarOpen]
  );

  // Guest users have no sidebar and use the full available width.
  const sidebarWidth =
    auth && !isGuest && isNonMobile
      ? (isSidebarOpen ? EXPANDED_WIDTH : COLLAPSED_WIDTH)
      : 0;
      
  return (
    <>
      {/* The drawer panel is position: fixed, so it takes no space in the layout */}
      {auth && !isGuest && (
        <Sidebar
          isNonMobile={isNonMobile}
          drawerWidth={`${EXPANDED_WIDTH}px`}
          isSidebarOpen={isSidebarOpen}
          setIsSidebarOpen={handleSidebarOpen}
          auth={auth}
        />
      )}

      <Box
        sx={{
          display: "flex",
          width: "100%",
          height: "100vh",
          "@supports (height: 100dvh)": { height: "100dvh" },
          overflow: "hidden",
        }}
      >
        <Box
          component="main"
          sx={{
            flex: 1,
            minWidth: 0,
            height: "100%",
            display: "flex",
            flexDirection: "column",
            marginLeft: `${sidebarWidth}px`,
            transition: "margin-left 300ms ease",
            overflow: "hidden",
            position: "relative",
            ...(!showSlideshow && (isHomePage || hasBackground) && homeBackground),
          }}
        >
          {showSlideshow && <HomeBackdrop />}

          {/* Above the slideshow. Only layered on Home: a z-index here would also cap fixed
              overlays of the other pages (Radar, Review) below the sidebar. */}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              ...(showSlideshow && { position: "relative", zIndex: 1 }),
            }}
          >
            <Navbar
              isSidebarOpen={isSidebarOpen}
              setIsSidebarOpen={handleSidebarOpen}
              transparent={isHomePage}
              hideSidebarToggle={isGuest}
            />

            <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden", ...pageScrollbarSx(theme) }}>
              <Outlet />
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default TopLeft;